// Partner apps (Niche Accounting) have no sign-in of their own: they send a
// signed-out visitor here with `?return_to=<their URL>`. Only the partner app's
// own origin is honoured, so this can never become an open redirect.
const NICHE_ORIGIN =
  import.meta.env.VITE_NICHE_ACCOUNTING_ORIGIN ||
  "https://niche-accounting.sylviq.io";

/** The URL to send the visitor back to, or null if absent or not allowed. */
export function safeReturnTo(
  search: string,
  allowedOrigin: string = NICHE_ORIGIN,
): string | null {
  const raw = new URLSearchParams(search).get("return_to");
  if (!raw) return null;
  let target: URL;
  try {
    target = new URL(raw);
  } catch {
    return null;
  }
  let allowed: URL;
  try {
    allowed = new URL(allowedOrigin);
  } catch {
    return null;
  }
  if (target.origin !== allowed.origin) return null;
  // Never carry credentials in the URL, whatever the origin says.
  if (target.username || target.password) return null;
  return target.toString();
}

/** Leave this app for an allowed `return_to`; true when a redirect started. */
export function followReturnTo(search: string): boolean {
  const target = safeReturnTo(search);
  if (!target) return false;
  window.location.assign(target);
  return true;
}

/**
 * Continue only an API-created MCP OAuth interaction after first-party login.
 * The fixed route and 256-bit base64url handle avoid accepting a caller URL.
 */
export function safeOAuthLoginContinuation(search: string): string | null {
  const values = new URLSearchParams(search).getAll("oauth_interaction");
  if (values.length !== 1 || !/^[A-Za-z0-9_-]{43}$/.test(values[0] ?? "")) {
    return null;
  }
  return `/mcp/consent?interaction=${encodeURIComponent(values[0])}`;
}

export function safeCliDeviceLoginContinuation(search: string): string | null {
  const values = new URLSearchParams(search).getAll("cli_device");
  return values.length === 1 && values[0] === "1" ? "/oauth/device" : null;
}

export function followPostLoginContinuation(search: string): boolean {
  const oauthPath = safeOAuthLoginContinuation(search);
  if (oauthPath) {
    window.location.assign(oauthPath);
    return true;
  }
  const cli = safeCliDeviceLoginContinuation(search);
  if (cli) {
    window.location.assign(cli);
    return true;
  }
  return followReturnTo(search);
}

/**
 * The local OAuth client callback is a pre-registered loopback URL. Even
 * though the API enforces exact redirect URI matching, validate the returned
 * URL again before the browser leaves the Console origin.
 */
export function safeMcpOAuthCallbackRedirect(
  value: string,
  expectedIssuer = "https://api.sylviq.io",
): string | null {
  let target: URL;
  try {
    target = new URL(value);
  } catch {
    return null;
  }
  if (
    target.protocol !== "http:" ||
    !["127.0.0.1", "localhost", "[::1]"].includes(target.hostname) ||
    !target.port ||
    target.username ||
    target.password ||
    target.hash
  ) {
    return null;
  }
  const params = target.searchParams;
  const state = params.getAll("state");
  const issuer = params.getAll("iss");
  const code = params.getAll("code");
  const error = params.getAll("error");
  const successful = code.length === 1 && Boolean(code[0]) && error.length === 0;
  const denied =
    error.length === 1 && error[0] === "access_denied" && code.length === 0;
  if (
    [...params.keys()].some((key) => !["state", "iss", "code", "error"].includes(key)) ||
    state.length !== 1 ||
    !state[0] ||
    state[0].length > 512 ||
    issuer.length !== 1 ||
    issuer[0] !== expectedIssuer ||
    !(successful || denied)
  ) {
    return null;
  }
  return target.toString();
}
